import React, { useState, useEffect } from "react";
import {
    Alert,
    Box,
    Breadcrumbs,
    Button,
    Card,
    CardContent,
    CardHeader,
    Chip,
    CircularProgress,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Link,
    List,
    ListItem,
    ListItemText,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from "@mui/material";
import {
    ArrowBack as ArrowBackIcon,
    Refresh as RefreshIcon,
    PlayArrow as PlayArrowIcon,
    KeyboardArrowDown as ArrowDownIcon,
    KeyboardArrowUp as ArrowUpIcon,
} from "@mui/icons-material";
import { useParams, useNavigate } from "react-router-dom";
import {
    getCompanyRequest,
    getRole,
    getRoleMatches,
    triggerRoleMatching,
    confirmRoleMatch,
    rejectRoleMatch,
    CompanyRequest,
    CompanyRequestRole,
    CompanyRequestRoleMatch,
    CompanyRequestStatus,
    MatchScore,
} from "../../api/companyRequestsApi";
import logger from "../../utils/logger";

const STATUS_COLORS: Record<CompanyRequestStatus, "default" | "primary" | "success" | "warning"> = {
    DRAFT: "default",
    ACTIVE: "primary",
    FILLED: "success",
    CLOSED: "warning",
};

const SCORE_COLORS: Record<MatchScore, "default" | "success" | "warning" | "error"> = {
    HIGH: "success",
    MEDIUM: "warning",
    LOW: "error",
};

export default function CompanyRequestRoleDetail() {
    const { id, roleId } = useParams<{ id: string; roleId: string }>();
    const navigate = useNavigate();
    const requestId = id ? parseInt(id) : 0;
    const targetRoleId = roleId ? parseInt(roleId) : 0;

    const [request, setRequest] = useState<CompanyRequest | null>(null);
    const [role, setRole] = useState<CompanyRequestRole | null>(null);
    const [matches, setMatches] = useState<CompanyRequestRoleMatch[]>([]);
    const [loading, setLoading] = useState(false);
    const [matchingLoading, setMatchingLoading] = useState(false);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [selectedMatch, setSelectedMatch] = useState<CompanyRequestRoleMatch | null>(null);
    const [actionType, setActionType] = useState<"confirm" | "reject" | null>(null);
    const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
    const [conflictDialog, setConflictDialog] = useState<{
        open: boolean;
        message?: string;
        conflictingRequestId?: number;
        conflictingRoleId?: number;
        conflictingBusinessName?: string;
    }>({ open: false });

    const toggleRow = (matchId: number) => {
        setExpandedRows((prev) => {
            const next = new Set(prev);
            if (next.has(matchId)) next.delete(matchId);
            else next.add(matchId);
            return next;
        });
    };

    const loadData = async () => {
        if (!requestId || !targetRoleId) return;
        setLoading(true);
        try {
            const [req, r, m] = await Promise.all([
                getCompanyRequest(requestId),
                getRole(requestId, targetRoleId),
                getRoleMatches(requestId, targetRoleId),
            ]);
            setRequest(req);
            setRole(r);
            setMatches(m);
        } catch {
            logger.error("Failed to load role detail");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, [requestId, targetRoleId]);

    const handleTriggerMatching = async () => {
        setMatchingLoading(true);
        try {
            const results = await triggerRoleMatching(requestId, targetRoleId);
            setMatches(results);
            // Reload role to refresh status badge
            const r = await getRole(requestId, targetRoleId);
            setRole(r);
        } catch {
            logger.error("Failed to trigger matching");
        } finally {
            setMatchingLoading(false);
        }
    };

    const openAction = (match: CompanyRequestRoleMatch, type: "confirm" | "reject") => {
        setSelectedMatch(match);
        setActionType(type);
        setConfirmDialogOpen(true);
    };

    const handleExecuteAction = async () => {
        if (!selectedMatch || !actionType) return;
        try {
            if (actionType === "confirm") {
                await confirmRoleMatch(requestId, targetRoleId, selectedMatch.id);
            } else {
                await rejectRoleMatch(requestId, targetRoleId, selectedMatch.id);
            }
            setConfirmDialogOpen(false);
            loadData();
        } catch (error: any) {
            if (actionType === "confirm" && error?.response?.status === 409) {
                const data = error.response.data ?? {};
                setConfirmDialogOpen(false);
                setConflictDialog({
                    open: true,
                    message: data.message,
                    conflictingRequestId: data.conflictingRequestId,
                    conflictingRoleId: data.conflictingRoleId,
                    conflictingBusinessName: data.conflictingBusinessName,
                });
                return;
            }
            logger.error(`Failed to ${actionType} match`);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!request || !role) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">Role not found</Alert>
                <Button onClick={() => navigate("/company-requests")} sx={{ mt: 2 }}>Back to List</Button>
            </Box>
        );
    }

    const rolePosition = (request.roles ?? []).findIndex((r) => r.id === role.id) + 1;
    const roleLabel = `Role ${rolePosition || "?"} — ${role.professionKey}`;

    const confirmedCount = matches.filter((m) => m.status === "CONFIRMED").length;
    const needed = role.numberOfWorkersNeeded ?? null;
    const slotFilled = needed != null && confirmedCount >= needed;

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <Tooltip title="Back to request">
                    <IconButton onClick={() => navigate(`/company-requests/${requestId}`)}>
                        <ArrowBackIcon />
                    </IconButton>
                </Tooltip>
                <Breadcrumbs>
                    <Link underline="hover" sx={{ cursor: "pointer" }} onClick={() => navigate("/company-requests")}>
                        Company Requests
                    </Link>
                    <Link underline="hover" sx={{ cursor: "pointer" }} onClick={() => navigate(`/company-requests/${requestId}`)}>
                        Request #{request.id} {request.businessName ? `— ${request.businessName}` : ""}
                    </Link>
                    <Typography color="text.primary">{roleLabel}</Typography>
                </Breadcrumbs>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
                <h1>{roleLabel}</h1>
                <Chip label={role.status} color={STATUS_COLORS[role.status]} />
            </Box>

            <Grid container spacing={3}>
                <Grid item xs={12} md={5}>
                    <Card>
                        <CardHeader title="Role Details" />
                        <CardContent>
                            <List dense>
                                <ListItem><ListItemText primary="Profession" secondary={role.professionKey} /></ListItem>
                                <ListItem><ListItemText primary="Workers Needed" secondary={role.numberOfWorkersNeeded ?? "-"} /></ListItem>
                                <ListItem><ListItemText primary="Min Experience" secondary={role.minYearsExperience != null ? `${role.minYearsExperience} years` : "-"} /></ListItem>
                                <ListItem><ListItemText primary="Description" secondary={role.requirementsDescription || "-"} /></ListItem>
                                <ListItem>
                                    <ListItemText
                                        primary="Last Assessed"
                                        secondary={role.lastAssessedAt ? new Date(role.lastAssessedAt).toLocaleString() : "Never"}
                                    />
                                </ListItem>
                            </List>

                            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>From parent request</Typography>
                            <List dense>
                                <ListItem><ListItemText primary="Business" secondary={request.businessName || "-"} /></ListItem>
                                <ListItem><ListItemText primary="Start Date" secondary={request.startDate ? new Date(request.startDate).toLocaleDateString() : "-"} /></ListItem>
                                <ListItem><ListItemText primary="End Date" secondary={request.endDate ? new Date(request.endDate).toLocaleDateString() : "-"} /></ListItem>
                                <ListItem><ListItemText primary="Location" secondary={request.location || "-"} /></ListItem>
                            </List>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={7}>
                    <Card>
                        <CardHeader
                            title="Match Results"
                            action={
                                <Box sx={{ display: "flex", gap: 1 }}>
                                    <Tooltip title="Refresh">
                                        <Button onClick={loadData} size="small" disabled={loading}><RefreshIcon /></Button>
                                    </Tooltip>
                                    <Tooltip title={role.status === "FILLED" ? "Role is filled — matching disabled until needed-count is increased or a confirmed match is freed up." : ""}>
                                        <span>
                                            <Button
                                                variant="contained"
                                                size="small"
                                                onClick={handleTriggerMatching}
                                                disabled={matchingLoading || role.status === "FILLED"}
                                            >
                                                {matchingLoading ? <CircularProgress size={20} /> : <><PlayArrowIcon sx={{ mr: 1 }} /> Run Matching</>}
                                            </Button>
                                        </span>
                                    </Tooltip>
                                </Box>
                            }
                        />
                        <CardContent>
                            {slotFilled && (
                                <Alert severity="success" sx={{ mb: 2 }}>
                                    Slot filled — {confirmedCount} of {needed} confirmed. Remaining suggestions are read-only.
                                </Alert>
                            )}
                            {matches.length === 0 ? (
                                <Alert severity="info">No matches yet. Run matching to find candidates.</Alert>
                            ) : (
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                                                <TableCell sx={{ width: 48 }} />
                                                <TableCell>Worker</TableCell>
                                                <TableCell>Score</TableCell>
                                                <TableCell>Status</TableCell>
                                                <TableCell align="center">Actions</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {matches.map((match) => {
                                                const expanded = expandedRows.has(match.id);
                                                return (
                                                    <React.Fragment key={match.id}>
                                                        <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
                                                            <TableCell sx={{ width: 48 }}>
                                                                <IconButton size="small" onClick={() => toggleRow(match.id)} aria-label={expanded ? "Collapse details" : "Expand details"}>
                                                                    {expanded ? <ArrowUpIcon /> : <ArrowDownIcon />}
                                                                </IconButton>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                                                                    <span>{match.workerName || match.workerId}</span>
                                                                    {match.status === "SUGGESTED" && match.committedElsewhereOnRequestId != null && (
                                                                        <Tooltip title={`Already confirmed on Request #${match.committedElsewhereOnRequestId}, role #${match.committedElsewhereOnRoleId} for overlapping dates — confirming here will be blocked.`}>
                                                                            <Chip
                                                                                size="small"
                                                                                color="warning"
                                                                                variant="outlined"
                                                                                label={`Booked on #${match.committedElsewhereOnRequestId}/role ${match.committedElsewhereOnRoleId}`}
                                                                            />
                                                                        </Tooltip>
                                                                    )}
                                                                </Box>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Chip label={match.score} color={SCORE_COLORS[match.score]} size="small" />
                                                            </TableCell>
                                                            <TableCell>
                                                                <Chip
                                                                    label={match.status}
                                                                    color={match.status === "CONFIRMED" ? "success" : match.status === "REJECTED" ? "error" : "default"}
                                                                    size="small"
                                                                />
                                                            </TableCell>
                                                            <TableCell align="center">
                                                                {match.status === "SUGGESTED" && !slotFilled && (
                                                                    <>
                                                                        <Button size="small" color="success" onClick={() => openAction(match, "confirm")}>Confirm</Button>
                                                                        <Button size="small" color="error" onClick={() => openAction(match, "reject")}>Reject</Button>
                                                                    </>
                                                                )}
                                                                {match.status === "SUGGESTED" && slotFilled && (
                                                                    <Chip label="Slot filled" size="small" variant="outlined" />
                                                                )}
                                                                {match.status === "CONFIRMED" && <Chip label="✓ Confirmed" color="success" size="small" />}
                                                                {match.status === "REJECTED" && <Chip label="✗ Rejected" color="error" size="small" />}
                                                            </TableCell>
                                                        </TableRow>
                                                        <TableRow>
                                                            <TableCell colSpan={5} sx={{ p: 0, borderBottom: expanded ? undefined : "unset" }}>
                                                                <Collapse in={expanded} timeout="auto" unmountOnExit>
                                                                    <Box sx={{ p: 2, backgroundColor: "#fafafa" }}>
                                                                        <Typography variant="subtitle2" gutterBottom>Why {match.score}?</Typography>
                                                                        <Typography variant="body2" sx={{ mb: 2 }}>
                                                                            {match.reason || <em>No reasoning returned by the AI.</em>}
                                                                        </Typography>
                                                                        <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", color: "text.secondary", fontSize: 12 }}>
                                                                            <span><strong>Worker ID:</strong> {match.workerId}</span>
                                                                            {match.createdAt && (
                                                                                <span><strong>Suggested:</strong> {new Date(match.createdAt).toLocaleString()}</span>
                                                                            )}
                                                                        </Box>
                                                                        <Box sx={{ mt: 2 }}>
                                                                            <Button size="small" variant="outlined" onClick={() => navigate(`/workers/${match.workerId}`)}>
                                                                                View full profile
                                                                            </Button>
                                                                        </Box>
                                                                    </Box>
                                                                </Collapse>
                                                            </TableCell>
                                                        </TableRow>
                                                    </React.Fragment>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
                <DialogTitle>{actionType === "confirm" ? "Confirm Match?" : "Reject Candidate?"}</DialogTitle>
                <DialogContent>
                    {actionType === "confirm"
                        ? "Are you sure you want to confirm this candidate? This action cannot be undone."
                        : "Are you sure you want to reject this candidate? This action cannot be undone."}
                    {selectedMatch && (
                        <Box sx={{ mt: 2, p: 2, backgroundColor: "#f5f5f5", borderRadius: 1 }}>
                            <strong>{selectedMatch.workerName || selectedMatch.workerId}</strong>
                            <br />
                            Score: <Chip label={selectedMatch.score} color={SCORE_COLORS[selectedMatch.score]} size="small" />
                            {selectedMatch.reason && (<><br />Reason: {selectedMatch.reason}</>)}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleExecuteAction} variant="contained" color={actionType === "confirm" ? "success" : "error"}>
                        {actionType === "confirm" ? "Confirm" : "Reject"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={conflictDialog.open} onClose={() => setConflictDialog({ open: false })}>
                <DialogTitle>Cannot confirm — worker already committed</DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        {conflictDialog.message ?? "This worker is already confirmed on another role/request whose dates overlap."}
                    </Alert>
                    {conflictDialog.conflictingRequestId != null && conflictDialog.conflictingRoleId != null && (
                        <Box sx={{ mt: 1 }}>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() => navigate(`/company-requests/${conflictDialog.conflictingRequestId}/roles/${conflictDialog.conflictingRoleId}`)}
                            >
                                View Role #{conflictDialog.conflictingRoleId} on Request #{conflictDialog.conflictingRequestId}
                                {conflictDialog.conflictingBusinessName ? ` (${conflictDialog.conflictingBusinessName})` : ""}
                            </Button>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConflictDialog({ open: false })}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
